import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  FileText,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  X,
  Building2,
  CheckCircle2,
  Clock,
  History,
  Save,
  BookOpen,
  Shield,
  Scale,
  AlertCircle,
  ChevronDown
} from 'lucide-react';
import Pagination from '../components/Pagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/*
 * Altam™ Operating Services
 * Bylaws & Policies Management
 * Copyright (c) 2026 Pecaro Consulting Inc. All rights reserved.
 */

export default function BylawsPolicies() {
  const { user, token } = useAuth();
  const [bylaws, setBylaws] = useState([]);
  const [categories, setCategories] = useState({});
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  
  // Filters
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAmendModal, setShowAmendModal] = useState(false);
  const [selectedBylaw, setSelectedBylaw] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    category: 'bylaw',
    title: '',
    description: '',
    content: '',
    version: '1.0',
    effective_date: new Date().toISOString().split('T')[0],
    status: 'draft'
  });
  
  // Amendment form
  const [amendForm, setAmendForm] = useState({
    changes_summary: '',
    new_content: '',
    new_version: '',
    effective_date: '',
    approved_date: '',
    vote_result: ''
  });
  
  const [saving, setSaving] = useState(false);
  
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const isResident = user?.role === 'resident';

  useEffect(() => {
    if (!isResident) {
      fetchProperties();
    } else {
      fetchBylaws();
    }
  }, []);

  useEffect(() => {
    if (selectedProperty || isResident) {
      fetchBylaws();
    }
  }, [selectedProperty, currentPage, categoryFilter, statusFilter]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/corporations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
      if (response.data.length > 0) {
        setSelectedProperty(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    }
  };

  const fetchBylaws = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, limit: 10 });
      if (selectedProperty && !isResident) params.append('entity_id', selectedProperty);
      if (categoryFilter) params.append('category', categoryFilter);
      if (statusFilter && !isResident) params.append('status', statusFilter);

      const response = await axios.get(`${API}/governance/bylaws?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBylaws(response.data.data);
      setCategories(response.data.categories || {});
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch bylaws:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBylaw = async () => {
    if (!form.title || !form.content || !form.effective_date) {
      alert('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        entity_id: selectedProperty,
        ...form
      };

      await axios.post(`${API}/governance/bylaws`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Bylaw created successfully!');
      setShowCreateModal(false);
      resetForm();
      fetchBylaws();
    } catch (error) {
      console.error('Failed to create bylaw:', error);
      alert('Failed to create bylaw. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateBylaw = async () => {
    if (!selectedBylaw) return;

    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        content: form.content,
        effective_date: form.effective_date
      };

      await axios.put(`${API}/governance/bylaws/${selectedBylaw.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Bylaw updated successfully!');
      setShowViewModal(false);
      setEditMode(false);
      fetchBylaws();
    } catch (error) {
      console.error('Failed to update bylaw:', error);
      alert('Failed to update bylaw. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAmendBylaw = async () => {
    if (!selectedBylaw || !amendForm.new_content || !amendForm.new_version) {
      alert('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/governance/bylaws/${selectedBylaw.id}/amend`, amendForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Amendment recorded successfully!');
      setShowAmendModal(false);
      setAmendForm({
        changes_summary: '',
        new_content: '',
        new_version: '',
        effective_date: '',
        approved_date: '',
        vote_result: ''
      });
      fetchBylaws();
    } catch (error) {
      console.error('Failed to amend bylaw:', error);
      alert('Failed to record amendment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleActivateBylaw = async (bylawId) => {
    if (!confirm('This will make this bylaw active and supersede any other active bylaws in the same category. Continue?')) return;

    try {
      await axios.post(`${API}/governance/bylaws/${bylawId}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Bylaw activated!');
      fetchBylaws();
    } catch (error) {
      console.error('Failed to activate bylaw:', error);
      alert('Failed to activate bylaw.');
    }
  };

  const handleDeleteBylaw = async (bylawId) => {
    if (!confirm('Are you sure you want to delete this bylaw? This action cannot be undone.')) return;

    try {
      await axios.delete(`${API}/governance/bylaws/${bylawId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Bylaw deleted!');
      setShowViewModal(false);
      fetchBylaws();
    } catch (error) {
      console.error('Failed to delete bylaw:', error);
      alert('Failed to delete bylaw.');
    }
  };

  const openViewModal = (bylaw) => {
    setSelectedBylaw(bylaw);
    setForm({
      category: bylaw.category,
      title: bylaw.title,
      description: bylaw.description || '',
      content: bylaw.content,
      version: bylaw.version,
      effective_date: bylaw.effective_date,
      status: bylaw.status
    });
    setEditMode(false);
    setShowViewModal(true);
  };

  const openAmendModal = (bylaw) => {
    setSelectedBylaw(bylaw);
    const currentVersion = parseFloat(bylaw.version) || 1.0;
    setAmendForm({
      changes_summary: '',
      new_content: bylaw.content,
      new_version: (currentVersion + 0.1).toFixed(1),
      effective_date: new Date().toISOString().split('T')[0],
      approved_date: '',
      vote_result: ''
    });
    setShowAmendModal(true);
  };

  const resetForm = () => {
    setForm({
      category: 'bylaw',
      title: '',
      description: '',
      content: '',
      version: '1.0',
      effective_date: new Date().toISOString().split('T')[0],
      status: 'draft'
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'declaration': return <Shield className="w-4 h-4" />;
      case 'bylaw': return <BookOpen className="w-4 h-4" />;
      case 'rule': return <Scale className="w-4 h-4" />;
      case 'policy': return <FileText className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'declaration': return 'bg-violet-100 text-violet-600';
      case 'bylaw': return 'bg-indigo-100 text-indigo-600';
      case 'rule': return 'bg-amber-100 text-amber-600';
      case 'policy': return 'bg-emerald-100 text-emerald-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-amber-100 text-amber-700',
      active: 'bg-emerald-100 text-emerald-700',
      superseded: 'bg-slate-100 text-slate-500',
      archived: 'bg-slate-100 text-slate-500'
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-6" data-testid="bylaws-policies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Bylaws & Policies</h1>
          <p className="text-slate-500">
            {isResident ? 'View your property\'s rules and regulations' : 'Manage bylaws, rules, and policies'}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Property Filter */}
          {!isResident && properties.length > 0 && (
            <select
              value={selectedProperty}
              onChange={(e) => {
                setSelectedProperty(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              {properties.map((prop) => (
                <option key={prop.id} value={prop.id}>{prop.name}</option>
              ))}
            </select>
          )}
          
          {isAdmin && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New Bylaw
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      {!isResident && (
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
          >
            <option value="">All Categories</option>
            {Object.entries(categories).map(([key, name]) => (
              <option key={key} value={key}>{name}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="superseded">Superseded</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      )}

      {/* Category Summary Cards (for residents) */}
      {isResident && bylaws.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(categories).map(([key, name]) => {
            const count = bylaws.filter(b => b.category === key).length;
            if (count === 0) return null;
            return (
              <div
                key={key}
                onClick={() => setCategoryFilter(categoryFilter === key ? '' : key)}
                className={`p-4 rounded-xl border cursor-pointer transition-all ${
                  categoryFilter === key
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <div className={`inline-flex p-2 rounded-lg ${getCategoryColor(key)} mb-2`}>
                  {getCategoryIcon(key)}
                </div>
                <p className="font-medium text-slate-900">{name}</p>
                <p className="text-sm text-slate-500">{count} document{count !== 1 ? 's' : ''}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Bylaws List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : bylaws.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-500">
            <BookOpen className="w-12 h-12 text-slate-300 mb-3" />
            <p>No bylaws or policies found</p>
            {isAdmin && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-3 text-indigo-600 hover:text-indigo-700 text-sm font-medium"
              >
                Create your first bylaw →
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {bylaws.map((bylaw) => (
              <div
                key={bylaw.id}
                className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                onClick={() => openViewModal(bylaw)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${getCategoryColor(bylaw.category)}`}>
                      {getCategoryIcon(bylaw.category)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-slate-900">{bylaw.title}</h3>
                        <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                          v{bylaw.version}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 mt-0.5">{bylaw.category_name}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                        <span>Effective: {formatDate(bylaw.effective_date)}</span>
                        {bylaw.amendments?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <History className="w-3 h-3" />
                            {bylaw.amendments.length} amendment{bylaw.amendments.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(bylaw.status)}`}>
                      {bylaw.status}
                    </span>
                    {isAdmin && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); openAmendModal(bylaw); }}
                          className="p-1 text-slate-400 hover:text-indigo-600 rounded"
                          title="Amend"
                        >
                          <History className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteBylaw(bylaw.id); }}
                          className="p-1 text-slate-400 hover:text-rose-600 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {pagination.pages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.pages}
          totalItems={pagination.total}
          itemsPerPage={10}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">Create New Bylaw / Policy</h2>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  >
                    {Object.entries(categories).map(([key, name]) => (
                      <option key={key} value={key}>{name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  placeholder="e.g., Pet Policy, Parking Rules"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  placeholder="Brief description of the bylaw/policy"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Version</label>
                  <input
                    type="text"
                    value={form.version}
                    onChange={(e) => setForm({ ...form, version: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    placeholder="1.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Effective Date *</label>
                  <input
                    type="date"
                    value={form.effective_date}
                    onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Content *</label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono text-sm"
                  rows={12}
                  placeholder="Enter the full text of the bylaw or policy..."
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 p-4 flex justify-end gap-3">
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateBylaw}
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Save className="w-4 h-4" />}
                Create Bylaw
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View/Edit Modal */}
      {showViewModal && selectedBylaw && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${getCategoryColor(selectedBylaw.category)}`}>
                  {getCategoryIcon(selectedBylaw.category)}
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">{selectedBylaw.title}</h2>
                  <p className="text-sm text-slate-500">Version {selectedBylaw.version} • {selectedBylaw.category_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(selectedBylaw.status)}`}>
                  {selectedBylaw.status}
                </span>
                {isAdmin && selectedBylaw.status === 'draft' && (
                  <button
                    onClick={() => handleActivateBylaw(selectedBylaw.id)}
                    className="px-3 py-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm"
                  >
                    Activate
                  </button>
                )}
                {isAdmin && !editMode && (
                  <button
                    onClick={() => setEditMode(true)}
                    className="px-3 py-1 border border-slate-300 rounded-lg text-sm hover:bg-slate-50"
                  >
                    Edit
                  </button>
                )}
                <button onClick={() => { setShowViewModal(false); setEditMode(false); }} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Metadata */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-slate-50 rounded-lg p-4">
                <div>
                  <p className="text-xs text-slate-500">Category</p>
                  <p className="font-medium text-slate-900">{selectedBylaw.category_name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Version</p>
                  <p className="font-medium text-slate-900">{selectedBylaw.version}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Effective Date</p>
                  <p className="font-medium text-slate-900">{formatDate(selectedBylaw.effective_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Amendments</p>
                  <p className="font-medium text-slate-900">{selectedBylaw.amendments?.length || 0}</p>
                </div>
              </div>

              {editMode ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Content</label>
                    <textarea
                      value={form.content}
                      onChange={(e) => setForm({ ...form, content: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono text-sm"
                      rows={15}
                    />
                  </div>
                </>
              ) : (
                <>
                  {selectedBylaw.description && (
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-1">Description</p>
                      <p className="text-slate-600">{selectedBylaw.description}</p>
                    </div>
                  )}
                  
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Content</p>
                    <div className="bg-slate-50 rounded-lg p-4 whitespace-pre-wrap text-sm text-slate-700 max-h-96 overflow-y-auto">
                      {selectedBylaw.content}
                    </div>
                  </div>

                  {/* Amendment History */}
                  {selectedBylaw.amendments?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                        <History className="w-4 h-4" />
                        Amendment History
                      </p>
                      <div className="space-y-2">
                        {selectedBylaw.amendments.map((amendment, idx) => (
                          <div key={amendment.id || idx} className="bg-slate-50 rounded-lg p-3 text-sm">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-slate-900">
                                v{amendment.previous_version} → v{amendment.new_version}
                              </span>
                              <span className="text-slate-500">{formatDate(amendment.effective_date)}</span>
                            </div>
                            <p className="text-slate-600">{amendment.changes_summary}</p>
                            {amendment.vote_result && (
                              <p className="text-xs text-slate-500 mt-1">Vote: {amendment.vote_result}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {editMode && (
              <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 p-4 flex justify-end gap-3">
                <button
                  onClick={() => setEditMode(false)}
                  className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateBylaw}
                  disabled={saving}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
                >
                  {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Save className="w-4 h-4" />}
                  Save Changes
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Amendment Modal */}
      {showAmendModal && selectedBylaw && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Amend Bylaw</h2>
                <p className="text-sm text-slate-500">{selectedBylaw.title} (Current: v{selectedBylaw.version})</p>
              </div>
              <button onClick={() => setShowAmendModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    Creating an amendment will update the bylaw content and increment the version number. 
                    The previous version will be preserved in the amendment history.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">New Version *</label>
                  <input
                    type="text"
                    value={amendForm.new_version}
                    onChange={(e) => setAmendForm({ ...amendForm, new_version: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Effective Date *</label>
                  <input
                    type="date"
                    value={amendForm.effective_date}
                    onChange={(e) => setAmendForm({ ...amendForm, effective_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Vote Result</label>
                  <input
                    type="text"
                    value={amendForm.vote_result}
                    onChange={(e) => setAmendForm({ ...amendForm, vote_result: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    placeholder="e.g., Passed 45-5"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Changes Summary *</label>
                <textarea
                  value={amendForm.changes_summary}
                  onChange={(e) => setAmendForm({ ...amendForm, changes_summary: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  rows={3}
                  placeholder="Describe what changed in this amendment..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">New Content *</label>
                <textarea
                  value={amendForm.new_content}
                  onChange={(e) => setAmendForm({ ...amendForm, new_content: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono text-sm"
                  rows={12}
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 p-4 flex justify-end gap-3">
              <button
                onClick={() => setShowAmendModal(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                onClick={handleAmendBylaw}
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <History className="w-4 h-4" />}
                Record Amendment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
